import { Rule, Zone } from "@fairfleet/geotab";
import { zoneTypeId } from "@/pages/zones/utils/zoneTypeId";

export const ZONELOG_GROUP_NAME = "Zonelogs";
export const ZONEVISIT_RULE_PREFIX = "[ZN] ";
export const ZONEVISIT_MARKER = "fairfleet:zonevisits:";
/**
 * Geotab's `Rule` name search (`%` wildcard). Without the "[": it opens a character class in the server's LIKE
 * (live fairfleet 2026-11-04: "%ZN] %" finds every [ZN] rule). Ours are then picked by marker (`zoneTypeIdOfRule`).
 */
export const ZONEVISIT_RULE_NAME_SEARCH = "%ZN] %";
/** Private addresses are never evaluated, whatever their zones' switch says. */
const EXCLUDED_ZONE_TYPES = new Set(["ZoneTypeHomeId"]);
const ZONEVISIT_RULE_COLOR = { r: 0, g: 121, b: 107, a: 255 };
const MAX_RULE_NAME_LENGTH = 255;

export interface ZoneTypeRef {
  id: string;
  name: string;
}

/**
 * The zone type a zone visit rule records, from its comment marker only. The name is no proof: a hand-made
 * „[ZN] …“ rule (live fairfleet 2026-11-04: zone type Kunde, whole company) is not ours, its events no visits.
 */
export function zoneTypeIdOfRule(rule: Pick<Rule, "comment"> & { name?: string; condition?: unknown }): string | undefined {
  return rule.comment?.match(/fairfleet:zonevisits:(\S+)/)?.[1];
}

/**
 * One rule per zone type: Geotab raises it while the vehicle is in any zone of that type. Zones of the same type
 * that touch merge into one visit (live fairfleet 2026-11-04); the zone is read from the position at its start.
 * A bare `InsideArea` root is rejected ("Inconsistent condition tree"); wrapped in `And` it is accepted.
 */
export function buildZoneVisitRule(type: ZoneTypeRef, groupId: string): Record<string, unknown> {
  return {
    name: `${ZONEVISIT_RULE_PREFIX}${type.name}`.slice(0, MAX_RULE_NAME_LENGTH),
    comment: `${ZONEVISIT_MARKER}${type.id}`,
    baseType: "Custom",
    groups: [{ id: groupId }],
    color: ZONEVISIT_RULE_COLOR,
    condition: { conditionType: "And", children: [{ conditionType: "InsideArea", zoneType: { id: type.id }, children: [] }] },
  };
}

export interface ZoneVisitSyncPlan {
  /** Zone types with at least one zone whose stop detection is on, still without a rule. */
  missing: ZoneTypeRef[];
  ruleByTypeId: Map<string, Rule>;
}

export function planZoneVisitSync(zones: Zone[], types: ZoneTypeRef[], rules: Rule[]): ZoneVisitSyncPlan {
  const ruleByTypeId = new Map<string, Rule>();
  for (const rule of rules) {
    const typeId = zoneTypeIdOfRule(rule);
    if (typeId && !ruleByTypeId.has(typeId)) ruleByTypeId.set(typeId, rule);
  }
  // Only zones with stop detection on are recorded; a type with none of them (e.g. Bundesländer) gets no rule.
  const used = new Set(
    zones.filter(zone => zone.mustIdentifyStops).flatMap(zone => (zone.zoneTypes ?? []).map(ref => zoneTypeId(ref))),
  );
  return {
    missing: types.filter(type => used.has(type.id) && !EXCLUDED_ZONE_TYPES.has(type.id) && !ruleByTypeId.has(type.id)),
    ruleByTypeId,
  };
}

const COMPANY_GROUP_ID = "GroupCompanyId";

/**
 * Setup is only offered to users who see the whole database: a user with a narrower data scope may not see
 * „Zonelogs“ or its rules, so the duplicate check would miss them and the visits would be recorded twice.
 */
export function hasFullDataScope(companyGroups: { id: string }[] | undefined): boolean {
  return !!companyGroups?.some(group => group.id === COMPANY_GROUP_ID);
}

/**
 * Zone types whose rule was created after `from` and that have no visit before that – before it, an empty
 * table would mean "not recorded", not "not there". A visit older than its rule comes from a reprocessing.
 */
export function lateRecordingRules(
  rules: Rule[],
  earliestVisitByRule: Map<string, Date>,
  /** Rules Geotab reprocessed (see `reprocessedRuleIds`): recorded before their creation too. */
  reprocessed: Set<string>,
  from: Date,
  typeNames: Map<string, string>,
): { name: string; from: Date }[] {
  return rules.flatMap(rule => {
    const start = new Date(rule.activeFrom);
    if (Number.isNaN(start.getTime()) || start <= from || reprocessed.has(rule.id)) return [];
    const earliest = earliestVisitByRule.get(rule.id);
    if (earliest && earliest < start) return [];
    const typeId = zoneTypeIdOfRule(rule);
    const name = (typeId && typeNames.get(typeId)) || rule.name.replace(ZONEVISIT_RULE_PREFIX, "");
    return [{ name, from: start }];
  });
}
